import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table } from 'reactstrap';
import { StatusBadge } from '../StatusBadge';
import { StatusTag } from './StatusTag';
import { ReassignMissionDialog, type ReassignOperator } from './ReassignMissionDialog';
import { ResetSplitDialog } from './ResetSplitDialog';
import type { PickListMission, PickListItem } from '../../api/pickLists';
import { useRole } from '../../auth/useRole';
import { formatDateTime, formatDuration } from './missionTiming';

export interface PickListMissionsSectionProps {
  missions: PickListMission[];
  /** Items array — used to look up nested tasks per mission. */
  items: PickListItem[];
  /** Gate the "Split lista" button on status=open. */
  pickListStatus?: string;
  /** Called when the user clicks "Split lista". */
  onOpenWizard?: () => void;
  // ── SP-5p: reassign / unassign controls (provided by PickListDetailPanel) ──
  /** Operators of the warehouse, for the reassign picker. */
  operators?: ReassignOperator[];
  /** Reassign a mission to a different operator (via /reassign). */
  onReassign?: (vars: { missionId: number; newOperatorUserId: number }) => Promise<void> | void;
  /** Remove the operator from a mission (via /unassign). */
  onUnassign?: (missionId: number) => Promise<void> | void;
  /** SP-6e: assign an operator to a pending (unassigned) mission (via /assign). */
  onAssign?: (vars: { missionId: number; operatorUserId: number }) => Promise<void> | void;
  /** SP-11b: close (finalize) a mission in awaiting_close state (via /close). */
  onCloseMission?: (missionId: number) => Promise<void> | void;
  /** SP-6f: "Ripristina lista" — undo the split (delete missions+tasks, reopen). */
  onResetSplit?: () => Promise<void> | void;
  /** Error surfaced from the last reset-split attempt (shown in the dialog). */
  resetSplitError?: string | null;
  /** Reset-split mutation in-flight. */
  resetSplitPending?: boolean;
  /** Label of the pick-list (for the confirmation dialog title). */
  pickListLabel?: string;
  /** Error surfaced from the last reassign attempt (shown in the dialog). */
  reassignError?: string | null;
  /** Reassign mutation in-flight. */
  reassignPending?: boolean;
}

export function PickListMissionsSection({
  missions, items, pickListStatus, onOpenWizard,
  operators, onReassign, onUnassign, onAssign, onCloseMission, reassignError, reassignPending,
  onResetSplit, resetSplitError, resetSplitPending, pickListLabel,
}: PickListMissionsSectionProps) {
  const { t, i18n } = useTranslation('picklist');
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [reassignTarget, setReassignTarget] = useState<PickListMission | null>(null);
  const [assignTarget, setAssignTarget] = useState<PickListMission | null>(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  // The backend split route is gated `rbac:warehouse-admin,write` (with
  // platform-admin pass-through). tenant-admin is NOT allowed to split — it
  // owns configuration, not operations (SP-5h). Showing the button to a
  // tenant-admin produced a 403 the wizard then swallowed, so the click
  // appeared to do nothing. Gate the button to match the backend exactly.
  const { isWarehouseAdmin, isPlatformAdmin } = useRole();
  const showSplitButton = pickListStatus === 'open'
    && (isWarehouseAdmin || isPlatformAdmin)
    && onOpenWizard !== undefined;

  // SP-5p: reassign/unassign are warehouse-admin (platform-admin pass-through)
  // operations, available only when the panel wires the callbacks + operators.
  const canManageAssign = (isWarehouseAdmin || isPlatformAdmin)
    && operators !== undefined && onReassign !== undefined;
  // SP-6e: assigning a pending (unassigned) mission from the detail page.
  const canAssign = (isWarehouseAdmin || isPlatformAdmin)
    && operators !== undefined && onAssign !== undefined;
  // SP-11b: chiusura esplicita di una missione awaiting_close (fallback admin).
  // Guard dedicata: non riusare canManageAssign (che dipende da operators/onReassign).
  const canClose = (isWarehouseAdmin || isPlatformAdmin) && onCloseMission !== undefined;
  // SP-6f: "Ripristina lista" guards — server enforces these too (defence in depth).
  const hasActiveMission = missions.some((m) => m.status === 'active');
  const hasDoneTask = items.some((it) => (it.tasks ?? []).some((t) => t.status === 'done'));
  const canResetSplit = (isWarehouseAdmin || isPlatformAdmin)
    && onResetSplit !== undefined
    && pickListStatus === 'in_progress'
    && missions.length > 0
    && !hasActiveMission
    && !hasDoneTask;

  if (missions.length === 0) {
    return (
      <section className="pl-acc-section pl-acc-section--missions">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <h5 className="vp-label mb-0">{t('missions.title_count', { count: 0 })}</h5>
          {showSplitButton && (
            <Button type="button" color="primary" size="sm" onClick={onOpenWizard}>
              {t('missions.split_button')}
            </Button>
          )}
        </div>
        <p>{t('missions.empty_not_split')}</p>
      </section>
    );
  }

  // Group tasks by mission_id. Tasks live nested inside items[].tasks[].
  type Task = NonNullable<PickListItem['tasks']>[number];
  const tasksByMission = new Map<number, Task[]>();
  for (const m of missions) tasksByMission.set(m.id, []);
  for (const it of items) {
    for (const t of it.tasks ?? []) {
      const arr = tasksByMission.get(t.mission_id);
      if (arr !== undefined) arr.push(t);
    }
  }

  function toggle(id: number): void {
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <section style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <h5 className="vp-label mb-0">
          {t('missions.title_count', { count: missions.length })}
        </h5>
        {showSplitButton && (
          <Button type="button" color="primary" size="sm" onClick={onOpenWizard}>
            {t('missions.split_button')}
          </Button>
        )}
        {canResetSplit && (
          <Button type="button" color="danger" outline size="sm"
                  onClick={() => setResetDialogOpen(true)}
                  title={t('missions.reset_split_tooltip')}>
            {t('missions.reset_split_button')}
          </Button>
        )}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12,
        marginTop: 16, marginBottom: 16,
      }}>
        {missions.map((m) => {
          const tasks = tasksByMission.get(m.id) ?? [];
          const isOpen = expanded.has(m.id);
          // Pending tasks on a mission with no operator render as
          // "Da assegnare"; with an operator they render as "In coda"
          // (queued, awaiting voice action).
          const missionUnassigned = m.operator_user_id === null;
          return (
            <article key={m.id} style={{
              padding: 12, background: 'var(--vp-surface)',
              border: '1px solid var(--vp-border)', borderRadius: 'var(--vp-r-md)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <strong>Mission {m.id}</strong>
                <StatusBadge tone={m.status === 'done' ? 'success'
                                  : m.status === 'active' ? 'info'
                                  : m.status === 'awaiting_close' ? 'warn'
                                  : m.status === 'cancelled' ? 'danger' : 'neutral'}>
                  {t(`missions.status.${m.status}`, { defaultValue: m.status })}
                </StatusBadge>
              </div>
              <div style={{ fontSize: 'var(--vp-text-sm)', color: 'var(--vp-text-2)' }}>
                {m.operator_email ?? t('missions.unassigned')}
              </div>
              <div style={{ fontSize: 'var(--vp-text-sm)' }}>
                {t('missions.tasks_count', { done: m.tasks_done, total: m.task_count })}
              </div>
              <dl className="vp-mission-timing" style={{
                display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 8, rowGap: 0,
                margin: '4px 0 0', fontSize: 'var(--vp-text-sm)',
              }}>
                <dt style={{ fontWeight: 400, color: 'var(--vp-text-2)' }}>{t('missions.started_at')}</dt>
                <dd className="mb-0">{formatDateTime(m.started_at, i18n.language)}</dd>
                <dt style={{ fontWeight: 400, color: 'var(--vp-text-2)' }}>{t('missions.completed_at')}</dt>
                <dd className="mb-0">{formatDateTime(m.completed_at, i18n.language)}</dd>
                <dt style={{ fontWeight: 400, color: 'var(--vp-text-2)' }}>{t('missions.duration')}</dt>
                <dd className="mb-0">{formatDuration(m.started_at, m.completed_at)}</dd>
              </dl>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <Button type="button" color="light" size="sm" onClick={() => toggle(m.id)}>
                  {isOpen ? t('missions.reduce') : t('missions.expand')}
                </Button>
                {canManageAssign && m.operator_user_id !== null && (m.status === 'assigned' || m.status === 'active') && (
                  <Button type="button" color="secondary" outline size="sm" onClick={() => setReassignTarget(m)}>
                    {t('missions.reassign')}
                  </Button>
                )}
                {canManageAssign && onUnassign && m.operator_user_id !== null && m.status === 'assigned' && (
                  <Button type="button" color="light" size="sm"
                          onClick={() => {
                            if (window.confirm(t('missions.unassign_confirm', { id: m.id, operator: m.operator_email ?? t('missions.operator_generic') }))) {
                              void onUnassign(m.id);
                            }
                          }}>
                    {t('missions.unassign')}
                  </Button>
                )}
                {canAssign && m.operator_user_id === null && m.status === 'pending' && (
                  <Button type="button" color="primary" outline size="sm" onClick={() => setAssignTarget(m)}>
                    {t('missions.assign')}
                  </Button>
                )}
                {canClose && m.status === 'awaiting_close' && (
                  <Button type="button" color="success" size="sm"
                          onClick={() => {
                            if (window.confirm(t('missions.close_confirm', { id: m.id }))) {
                              void onCloseMission?.(m.id);
                            }
                          }}>
                    {t('missions.close')}
                  </Button>
                )}
              </div>
              {isOpen && (
                <div className="table-responsive">
                <Table className="table-sm align-middle table-striped mt-2 mb-0">
                  <thead>
                    <tr>
                      <th>{t('missions.task_col')}</th><th>{t('missions.qty_assigned_col')}</th><th>{t('missions.picked_col')}</th><th>{t('missions.status_col')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map((t) => {
                      // Partial pick (operator used `forza N` with N < qty_assigned).
                      // Render in red as "picked/assigned" so the warehouse-admin
                      // immediately spots short picks during sign-off.
                      const partial = t.qty_picked != null && t.qty_picked < t.qty_assigned;
                      return (
                        <tr key={t.task_id}>
                          <td>{t.task_id}</td>
                          <td>{t.qty_assigned}</td>
                          <td style={partial ? { color: 'var(--vp-danger, #b00020)', fontWeight: 600 } : undefined}>
                            {t.qty_picked == null
                              ? 0
                              : partial
                                ? `${t.qty_picked}/${t.qty_assigned}`
                                : t.qty_picked}
                          </td>
                          <td><StatusTag status={t.status} unassigned={missionUnassigned} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {reassignTarget && onReassign && operators && (
        <ReassignMissionDialog
          isOpen
          missionId={reassignTarget.id}
          missionLabel={`Mission ${reassignTarget.id}`}
          isActive={reassignTarget.status === 'active'}
          currentOperatorUserId={reassignTarget.operator_user_id}
          operators={operators}
          pending={reassignPending}
          error={reassignError ?? null}
          onCancel={() => setReassignTarget(null)}
          onSubmit={async ({ newOperatorUserId }) => {
            try {
              await onReassign({ missionId: reassignTarget.id, newOperatorUserId });
              setReassignTarget(null);
            } catch {
              // error is surfaced via the reassignError prop set by the parent
            }
          }}
        />
      )}

      {onResetSplit && (
        <ResetSplitDialog
          isOpen={resetDialogOpen}
          pickListLabel={pickListLabel ?? ''}
          missionsCount={missions.length}
          pending={resetSplitPending}
          error={resetSplitError ?? null}
          onCancel={() => setResetDialogOpen(false)}
          onSubmit={async () => {
            try {
              await onResetSplit();
              setResetDialogOpen(false);
            } catch {
              // error surfaced via resetSplitError prop
            }
          }}
        />
      )}

      {assignTarget && onAssign && operators && (
        <ReassignMissionDialog
          isOpen
          mode="assign"
          missionId={assignTarget.id}
          missionLabel={`Mission ${assignTarget.id}`}
          isActive={false}
          currentOperatorUserId={null}
          operators={operators}
          pending={reassignPending}
          error={reassignError ?? null}
          onCancel={() => setAssignTarget(null)}
          onSubmit={async ({ newOperatorUserId }) => {
            try {
              await onAssign({ missionId: assignTarget.id, operatorUserId: newOperatorUserId });
              setAssignTarget(null);
            } catch {
              // error surfaced via reassignError prop
            }
          }}
        />
      )}
    </section>
  );
}
